import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const UUID_REGEX =
  /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;
const SHORT_UUID_REGEX = /^[A-Za-z0-9_-]{22}$/;

function bytesToBase64(bytes: Uint8Array): string {
  const nodeBuffer = (
    globalThis as { Buffer?: typeof import("node:buffer").Buffer }
  ).Buffer;
  if (nodeBuffer) {
    return nodeBuffer.from(bytes).toString("base64");
  }

  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const nodeBuffer = (
    globalThis as { Buffer?: typeof import("node:buffer").Buffer }
  ).Buffer;
  if (nodeBuffer) {
    return Uint8Array.from(nodeBuffer.from(base64, "base64"));
  }

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function encodeUuidForPath(uuid: string): string {
  if (!UUID_REGEX.test(uuid)) {
    throw new Error(`Invalid UUID: ${uuid}`);
  }

  const hex = uuid.replace(/-/g, "");
  const pairs = hex.match(/.{2}/g);
  if (!pairs || pairs.length !== 16) {
    throw new Error(`Invalid UUID bytes: ${uuid}`);
  }
  const bytes = new Uint8Array(pairs.map((pair) => Number.parseInt(pair, 16)));
  const base64 = bytesToBase64(bytes);

  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeShortUuid(shortId: string): string {
  const base64 = shortId.replace(/-/g, "+").replace(/_/g, "/") + "==";
  const bytes = base64ToBytes(base64);
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
}

export function decodeUuidFromPath(pathId: string): string {
  if (UUID_REGEX.test(pathId)) {
    return pathId.toLowerCase();
  }

  if (!SHORT_UUID_REGEX.test(pathId)) {
    throw new Error(`Invalid UUID path id: ${pathId}`);
  }

  const uuid = decodeShortUuid(pathId);
  if (!UUID_REGEX.test(uuid)) {
    throw new Error(`Decoded invalid UUID path id: ${pathId}`);
  }

  return uuid.toLowerCase();
}

export const parseMeasuringPointType = (
  type: "Electricity" | "Heat" | "Cold" | "Water" | "Gas" | "Unknown",
) => {
  switch (type) {
    case "Electricity":
      return {
        className: "bg-electricity text-electricityText",
        text: "Elektřina",
      };
    case "Heat":
      return {
        className: "bg-warmth text-warmthText",
        text: "Teplo",
      };
    case "Cold":
      return {
        className: "bg-cold text-coldText",
        text: "Chlad",
      };
    case "Water":
      return {
        className: "bg-water text-waterText",
        text: "Voda",
      };
    case "Gas":
      return { className: "bg-gas text-gasText", text: "Plyn" };
    default:
      return {
        className: "bg-slate-100 text-slate-700",
        text: "Neuvedeno",
      };
  }
};
